import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Search, Star, TrendingUp, ArrowRight, UserCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useIsMobile } from '@/hooks/use-mobile';
import React from 'react';

// Detect prefers-reduced-motion once
const usePrefersReducedMotion = () => {
    const [reduced, setReduced] = useState(false);
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        setReduced(mq.matches);
        const onChange = () => setReduced(mq.matches);
        mq.addEventListener?.('change', onChange);
        return () => mq.removeEventListener?.('change', onChange);
    }, []);
    return reduced;
};

// Component for a single review card, styled with a strong glassmorphism effect.
const ReviewCard = ({ name, title, rating, content, className, animationProps }) => (
    <motion.div
        className={`absolute bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-md shadow-xl shadow-black/40 w-[350px] ${className}`}
        {...animationProps}
    >
        <div className="flex items-center mb-3">
            <UserCircle2 className="w-8 h-8 text-gray-300 mr-3 flex-shrink-0" />
            <div>
                <p className="font-bold text-white text-left">{name}</p>
                <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                        <Star key={i} size={16} className={i < rating ? "text-yellow-400 fill-yellow-400" : "text-gray-500"} />
                    ))}
                </div>
            </div>
        </div>
        <h4 className="font-semibold text-lg text-white mb-2 text-left">{title}</h4>
        <p className="text-gray-300 text-sm text-left">{content}</p>
    </motion.div>
);

// A cleaner, more elegant showcase of floating review cards, positioned to avoid the center.
const ProductShowcase = () => {
    const reviews = [
        { name: "McDaniels Okoro", rating: 5, title: "Top Tier Firm", content: "Blue Guardian is a solid prop firm with fair targets...", className: "top-[5%] left-[5%]", animation: { y: [-10, 10] } },
        { name: "sandeep das", rating: 5, title: "Excellent", content: "I used a challenge account before. Their spreads were excellent...", className: "top-[15%] right-[5%]", animation: { y: [15, -5] } },
        { name: "Kyro De Leon", rating: 5, title: "Great customer support", content: "Atlas Funded stands out as one of the most transparent...", className: "bottom-[15%] left-[12%]", animation: { y: [5, -15] } },
        { name: "Umair Zia", rating: 5, title: "Great Firm", content: "I’m funded with a $10K account at Atlas Funded...", className: "bottom-[5%] right-[10%]", animation: { y: [-12, 8] } },
        { name: "saeed siddique", rating: 5, title: "Transparent & Fair", content: "I’ve had a great experience with Atlas Funded so far...", className: "top-[45%] left-[18%]", animation: { y: [8, -12] } },
        { name: "Tuanu Kabari", rating: 5, title: "Fast customer service", content: "Blue Guardian is a reliable prop firm with smooth processes...", className: "top-[2%] right-[30%]", animation: { y: [-8, 12] } },
    ];

    return (
        // This container is hidden on mobile (screens smaller than lg) to prevent clutter
        <div className="absolute inset-0 z-10 [perspective:1000px] hidden lg:block">
            {reviews.map((review, i) => (
                <ReviewCard
                    key={i}
                    {...review}
                    animationProps={{
                        initial: { opacity: 0, y: 50, scale: 0.9 },
                        animate: { opacity: 1, y: review.animation.y, scale: 1 },
                        transition: {
                            duration: 20 + i * 4,
                            repeat: Infinity,
                            repeatType: "mirror",
                            ease: "easeInOut",
                            delay: i * 2.5
                        }
                    }}
                />
            ))}
        </div>
    );
};


const ModernHeroSection = () => {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const reducedMotion = usePrefersReducedMotion();
    const [stats, setStats] = useState({ firms: 10, reviews: 100, traders: 5000 });

    // Memoize random sparkle positions so they don't re-randomize every render
    const sparkles = useMemo(
        () =>
            Array.from({ length: 12 }, (_, i) => ({
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                yFrom: Math.random() * 40 - 20,
                yTo: Math.random() * 40 - 20,
                xFrom: Math.random() * 40 - 20,
                xTo: Math.random() * 40 - 20,
                duration: 4 + Math.random() * 8,
                delay: i * 0.5,
            })),
        [],
    );

    useEffect(() => {
        // Skip stats fetch on mobile for better LCP
        if (isMobile) return;
        
        const fetchStats = async () => {
            try {
                const { count: firmsCount } = await supabase.from('firms').select('*', { count: 'exact', head: true }).eq('approved', true);
                const { count: reviewsCount } = await supabase.from('reviews').select('*', { count: 'exact', head: true }).eq('status', 'approved');
                const { count: tradersCount } = await supabase.from('user_roles').select('*', { count: 'exact', head: true }).eq('role', 'trader');
                setStats({
                    firms: Math.max(firmsCount || 0, 10),
                    reviews: Math.max(reviewsCount || 0, 100),
                    traders: Math.max(tradersCount || 0, 5000)
                });
            } catch (error) {
                console.error('Error fetching stats:', error);
            }
        };
        
        // Defer stats loading to improve LCP on desktop
        const timeout = setTimeout(fetchStats, 100);
        return () => clearTimeout(timeout);
    }, [isMobile]);

    const handleBrowseFirms = () => navigate('/browsefirms');
    const handleSubmitReview = () => navigate('/auth');

    return (
        <section className="relative bg-gray-900 text-white min-h-screen flex items-center justify-center overflow-hidden">
            {/* Glassy background with animated blurs and sparkles - Simplified on mobile */}
            <div className="absolute inset-0 z-0">
                <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-900 to-blue-900/20" />
                {!isMobile && !reducedMotion ? (
                    <>
                        {/* Main gradient blur */}
                        <motion.div
                            className="absolute top-1/4 left-1/2 w-[400px] h-[400px] md:w-[600px] md:h-[600px] bg-gradient-to-r from-electric-blue/20 via-blue-500/10 to-cyan-400/15 rounded-full blur-3xl"
                            animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.5, 0.2], x: [-50, 50, -50], y: [-30, 30, -30] }}
                            transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
                        />
                        {/* Secondary blur */}
                        <motion.div
                            className="absolute bottom-1/3 right-1/4 w-[300px] h-[300px] md:w-[400px] md:h-[400px] bg-gradient-to-l from-electric-blue/15 to-purple-500/10 rounded-full blur-2xl"
                            animate={{ scale: [1.2, 1, 1.2], opacity: [0.1, 0.4, 0.1], rotate: [0, 180, 360] }}
                            transition={{ duration: 20, repeat: Infinity, ease: "easeInOut", delay: 3 }}
                        />
                        {/* Floating sparkles - memoized positions */}
                        {sparkles.map((s, i) => (
                            <motion.div
                                key={i}
                                className="absolute w-1 h-1 md:w-2 md:h-2 bg-electric-blue/50 rounded-full"
                                style={{ left: s.left, top: s.top }}
                                animate={{ y: [s.yFrom, s.yTo], x: [s.xFrom, s.xTo], opacity: [0.1, 0.8, 0.1], scale: [1, 1.5, 1] }}
                                transition={{ duration: s.duration, repeat: Infinity, repeatType: "mirror", delay: s.delay, ease: "easeInOut" }}
                            />
                        ))}
                    </>
                ) : (
                    <>
                        {/* Static gradients on mobile for better performance */}
                        <div className="absolute top-1/4 left-1/2 w-[400px] h-[400px] bg-gradient-to-r from-electric-blue/20 via-blue-500/10 to-cyan-400/15 rounded-full blur-3xl opacity-30" />
                        <div className="absolute bottom-1/3 right-1/4 w-[300px] h-[300px] bg-gradient-to-l from-electric-blue/15 to-purple-500/10 rounded-full blur-2xl opacity-20" />
                    </>
                )}
            </div>
            
            {/* Product Showcase - now hidden on mobile and positioned away from center */}
            <ProductShowcase />
            
            {/* Gradient overlay to create depth */}
            <div className="absolute inset-0 z-20 bg-black/40" />

            {/* Foreground content */}
            <div className="container mx-auto px-4 text-center relative z-30">
                <motion.div
                    initial={{ opacity: 0, y: isMobile ? 20 : 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: isMobile ? 0.4 : 1, delay: isMobile ? 0 : 0.2 }}
                    className="max-w-6xl mx-auto"
                >
                    {/* Badge */}
                    <motion.div
                        initial={{ opacity: 0, scale: isMobile ? 1 : 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: isMobile ? 0.3 : 0.6, delay: isMobile ? 0.1 : 0.4 }}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-electric-blue/10 border border-electric-blue/20 text-electric-blue text-sm font-medium mb-8 backdrop-blur-sm"
                    >
                        <TrendingUp size={16} />
                        <span>Trusted by {stats.traders.toLocaleString()}+ Active Traders</span>
                    </motion.div>

                    <motion.h1
                        className="text-5xl md:text-7xl lg:text-8xl font-bold mb-8 leading-[1.1] md:leading-[0.9] tracking-tight"
                        initial={{ opacity: 0, y: isMobile ? 15 : 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: isMobile ? 0.4 : 0.8, delay: isMobile ? 0.15 : 0.6 }}
                    >
                        <span className="block text-white">Find Your</span>
                        <span className="block bg-gradient-to-r from-electric-blue via-blue-400 to-cyan-300 bg-clip-text text-transparent">
                            Perfect Prop Firm
                        </span>
                    </motion.h1>
                </motion.div>
                
                <motion.p
                    className="text-lg md:text-xl text-gray-300 mb-12 max-w-3xl mx-auto font-light leading-relaxed"
                    initial={{ opacity: 0, y: isMobile ? 10 : 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: isMobile ? 0.3 : 0.8, delay: isMobile ? 0.2 : 0.8 }}
                >
                    Compare challenges, read verified reviews, and make data-driven decisions.
                    <br />
                    <span className="text-electric-blue font-medium">Your trading success starts here.</span>
                </motion.p>
                
                <motion.div
                    className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16"
                    initial={{ opacity: 0, y: isMobile ? 10 : 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: isMobile ? 0.3 : 0.8, delay: isMobile ? 0.25 : 1 }}
                >
                    {isMobile ? (
                        <>
                            <Button onClick={handleBrowseFirms} aria-label="Browse and explore prop firms" className="w-full max-w-sm group bg-gradient-to-r from-electric-blue to-blue-500 text-dark-bg px-8 py-4 rounded-2xl font-bold text-lg shadow-xl shadow-electric-blue/25 border-0">
                                <Search size={20} className="mr-3" />
                                Explore Prop Firms
                                <ArrowRight size={20} className="ml-3" />
                            </Button>
                            
                            <Button onClick={handleSubmitReview} aria-label="Share your trading experience and review" variant="outline" className="w-full max-w-sm group border-2 border-electric-blue text-electric-blue px-8 py-4 rounded-2xl font-bold text-lg bg-gray-900/80 hover:bg-gray-800/80">
                                <Star size={20} className="mr-3" />
                                Share Your Experience
                            </Button>
                        </>
                    ) : (
                        <>
                            <motion.div whileHover={{ scale: 1.05, boxShadow: "0 25px 50px rgba(0, 191, 255, 0.3)" }} whileTap={{ scale: 0.95 }}>
                                <Button onClick={handleBrowseFirms} aria-label="Browse and explore prop firms" className="group bg-gradient-to-r from-electric-blue to-blue-500 text-dark-bg px-8 py-4 md:px-10 rounded-2xl font-bold text-lg hover:from-electric-blue/90 hover:to-blue-500/90 shadow-xl shadow-electric-blue/25 transition-all duration-300 border-0">
                                    <Search size={20} className="mr-3 group-hover:rotate-12 transition-transform duration-300" />
                                    Explore Prop Firms
                                    <ArrowRight size={20} className="ml-3 group-hover:translate-x-1 transition-transform duration-300" />
                                </Button>
                            </motion.div>
                            
                            <motion.div whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(0, 191, 255, 0.2)" }} whileTap={{ scale: 0.95 }}>
                                <Button onClick={handleSubmitReview} aria-label="Share your trading experience and review" variant="outline" className="group border-2 border-electric-blue text-electric-blue px-8 py-4 md:px-10 rounded-2xl font-bold text-lg hover:bg-electric-blue/10 backdrop-blur-sm transition-all duration-300 bg-gray-900/80">
                                    <Star size={20} className="mr-3 group-hover:fill-current transition-all duration-300" />
                                    Share Your Experience
                                </Button>
                            </motion.div>
                        </>
                    )}
                </motion.div>

                {/* Trust indicators */}
                <motion.div
                    className="flex flex-wrap justify-center items-center gap-4 md:gap-8 text-sm"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: isMobile ? 0.3 : 0.8, delay: isMobile ? 0.3 : 1.4 }}
                >
                    {[
                        { icon: TrendingUp, text: `${stats.reviews.toLocaleString()}+ Reviews`, color: "text-green-400" },
                        { icon: Star, text: `${stats.firms}+ Verified Firms`, color: "text-yellow-400" },
                        { icon: Search, text: "250+ Challenges", color: "text-blue-400" },
                    ].map((item, index) => (
                        <div
                            key={index}
                            className="flex items-center gap-3 px-4 py-2 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10"
                        >
                            <item.icon size={18} className={item.color} />
                            <span className="text-gray-300 font-medium">{item.text}</span>
                        </div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

export default ModernHeroSection;
